import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../contexts/ProductsContext';
import { useBundles } from '../contexts/BundlesContext';
import type { Product } from '../types/product';
import type { Bundle } from '../types/bundle';
import { useModalLayer } from '../hooks/useModalLayer';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type SearchResult = { type: 'product'; data: Product } | { type: 'bundle'; data: Bundle };

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  useModalLayer(isOpen, onClose);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { getSamplePacks, getPresets, products, getProductDetailPath: getPath } = useProducts();
  const { getBundles } = useBundles();

  const results = useMemo((): SearchResult[] => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const allProducts: Product[] = [...getSamplePacks(), ...getPresets(), ...products.filter((p) => p.type === 'template')];
    const bundles: Bundle[] = getBundles();
    const match = (name: string, desc: string) =>
      name.toLowerCase().includes(q) || desc.toLowerCase().includes(q);
    const productResults: SearchResult[] = allProducts
      .filter((p) => match(p.name, p.description))
      .map((data) => ({ type: 'product' as const, data }));
    const bundleResults: SearchResult[] = bundles
      .filter((b) => match(b.name, b.description))
      .map((data) => ({ type: 'bundle' as const, data }));
    return [...productResults, ...bundleResults];
  }, [query, getSamplePacks, getPresets, products, getBundles]);

  const handleSelect = (result: SearchResult) => {
    if (result.type === 'product') {
      navigate(getPath(result.data));
    } else {
      navigate(`/bundles/${result.data.slug}`);
    }
    onClose();
  };

  const getPrice = (r: SearchResult) =>
    r.type === 'product' ? r.data.price : r.data.price;
  const getOriginalPrice = (r: SearchResult) =>
    r.type === 'product' ? r.data.originalPrice : r.data.originalPrice;
  const typeLabel = (r: SearchResult) =>
    r.type === 'bundle' ? 'Bundle' : r.data.type.replace('_', ' ');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
            aria-hidden
          />

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-0 z-50 bg-dark-lighter shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-label="Search products and bundles"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="py-4 relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full bg-dark pl-12 pr-4 py-3 rounded-lg border border-white/10 focus:outline-none focus:border-neon-blue transition-colors"
                    placeholder="Search products and bundles..."
                    autoFocus
                    aria-label="Search"
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 hover:bg-white/5 rounded-full transition-colors"
                    aria-label="Close search"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="max-h-[60vh] overflow-y-auto border-t border-white/10">
                {results.length > 0 ? (
                  <div className="py-4 space-y-2">
                    {results.map((result) => (
                      <button
                        key={result.type + result.data.id}
                        type="button"
                        onClick={() => handleSelect(result)}
                        className="w-full p-4 flex items-start hover:bg-white/5 transition-colors rounded-lg text-left"
                      >
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium mb-1">{result.data.name}</h3>
                          <p className="text-sm text-gray-400 truncate">{result.data.description}</p>
                          <span className="text-xs text-gray-500 capitalize">{typeLabel(result)}</span>
                        </div>
                        <div className="ml-4 text-right flex-shrink-0">
                          <p className="font-medium">${getPrice(result).toFixed(2)}</p>
                          {getOriginalPrice(result) > getPrice(result) && (
                            <p className="text-sm text-gray-500 line-through">
                              ${getOriginalPrice(result).toFixed(2)}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : query.trim() ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-400 mb-4">No results found for &quot;{query}&quot;</p>
                    <p className="text-sm text-gray-500">Try different keywords or browse our categories below.</p>
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <p className="text-gray-400 mb-4">Type to search products and bundles</p>
                    <p className="text-sm text-gray-500">Sample packs, presets, templates, and bundles</p>
                  </div>
                )}
              </div>

              <div className="py-4 border-t border-white/10">
                <h3 className="text-sm font-medium text-gray-400 mb-3">Try searching for</h3>
                <div className="flex flex-wrap gap-2">
                  {['Trap', 'Preset', 'Bundle', 'Sample', 'Template', 'EDM'].map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => setQuery(term)}
                      className="px-3 py-1 text-sm bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;
